import { ClearDiv, EditableTable, Modal, Select } from '@aon/aon-tws-common-ui';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { ISurveyTermEditModel } from '../../../models/ISurveyTermEditModel';
import { ISurveyTermItemEditModel } from '../../../models/ISurveyTermItemEditModel';
import { IApplicationState } from '../../../store';
import _ from 'lodash';
import { defaultEditItem } from '../../../store/SurveyStore';

export const Content = styled.div`
	width: 100%;
`;

const LanguageSelectContainer = styled.div`
	text-align: right;
	width: 100%;
	position: relative;

	@media (max-width: 795px) {
		margin-top: 10px;
	}
`;

export interface ISurveyDefinitionsPreviewModalProps {
	surveyId: number;
	surveyTerm: ISurveyTermEditModel;
	surveyTermItems: ISurveyTermItemEditModel[];
	supportedCultures: string[];
	defaultCultureCode: string;
	isOpen: boolean;
	onClose: () => void;
}

export function SurveyDefinitionsPreviewModal(props: ISurveyDefinitionsPreviewModalProps) {
	const { surveyId, surveyTerm, surveyTermItems, isOpen, supportedCultures, defaultCultureCode, onClose } = props;

	const availableCultures = useSelector((state: IApplicationState) => state.cultures.availableCultures);
	const translations = useSelector((state: IApplicationState) => state.translations);
	const [selectedCultureCode, setSelectedCultureCode] = useState(defaultCultureCode);
	const [surveyTermState, setSurveyTermState] = useState<ISurveyTermEditModel>({ ...surveyTerm, surveyTermItems });

	const supportedLanguages = availableCultures
		.filter((c) => supportedCultures.findIndex((code) => code == c.name) >= 0)
		.map((lang) => {
			return {
				name: lang.name,
				displayText: lang.displayText,
			};
		});

	useEffect(() => {
		const fetchData = async () => {
			if (selectedCultureCode === defaultCultureCode) {
				setSurveyTermState({ ...surveyTerm, surveyTermItems });
			} else {
				const surveyTermItemsWithDefaultCulture = surveyTermItems.map((item) => {
					const termTranslations = translations.instances[`survey-term-items/${item.surveyTermItemId}/term`]?.translations;
					const descriptionTranslations = translations.instances[`survey-term-items/${item.surveyTermItemId}/description`]?.translations;

					return {
						...item,
						term: {
							...defaultEditItem,
							cultureCode: selectedCultureCode,
							textValue: _.find(termTranslations, (t) => t.cultureCode === selectedCultureCode)?.textValue || item.term?.textValue || '',
						},
						description: {
							...defaultEditItem,
							cultureCode: selectedCultureCode,
							textValue:
								_.find(descriptionTranslations, (t) => t.cultureCode === selectedCultureCode)?.textValue ||
								item.description?.textValue ||
								'',
						},
					};
				});

				const pageDescTranslations = translations.instances[`survey-term/page-description`]?.translations;
				setSurveyTermState({
					...surveyTerm,
					pageDescription: {
						...surveyTerm.pageDescription,
						textValue: _.find(pageDescTranslations, (t) => t.cultureCode === selectedCultureCode)?.textValue || '',
					},
					surveyTermItems: surveyTermItemsWithDefaultCulture,
				});
			}
		};

		fetchData();
	}, [defaultCultureCode, selectedCultureCode, surveyId, surveyTerm, surveyTermItems, translations.instances]);

	// With latest version (0.1.70055) of @aon/aon-tws-common-ui, the with/minWith/maxWidth do not work in EditableTable if editMode=false
	// Because it uses TableColumn when editMode=false and it doesn't pass over with/minWith/maxWidth to the TableColumn component
	// See
	// 		Web\src\components\EditableTable\EditableRows.tsx, line 112 in Common UI project
	// and
	//		Web\src\components\EditableTable\SharedComponents.tsx, line 40~56, in Common UI project
	// So is the EditableHeadColumnContainer, see
	//		Web\src\components\EditableTable\EditableHeadColumn.tsx, line 5~16 in Common UI project
	function mapColumns(def?: ISurveyTermItemEditModel) {
		return [
			{ columnKey: 'term', minWidth: '30%', label: def?.term?.textValue },
			{ columnKey: 'description', minWidth: '70%', label: def?.description?.textValue },
		];
	}

	function mapData(surveyDefinitions?: ISurveyTermItemEditModel[]) {
		return surveyDefinitions?.map((def) => {
			return {
				term: def?.term?.textValue || '',
				description: def?.description?.textValue || '',
			};
		});
	}

	function handleOnSubmit() {
		onClose();
	}

	const items = mapData(surveyTermState?.surveyTermItems?.filter((def) => def.sequence > 1));
	const columns = mapColumns(surveyTermState?.surveyTermItems?.find((def) => def.sequence === 1));
	return (
		<Modal
			isOpen={isOpen}
			hideFooter={false}
			hideHeader={false}
			contenthcentered={true}
			contentfullwidth={true}
			title={'Survey Definitions Preview'}
			onSubmit={handleOnSubmit}
			primaryButtonText={ContentConstants.BUTTONS_CLOSE}
			onClosed={onClose}
		>
			<Content>
				<LanguageSelectContainer>
					<Select
						disabled={supportedCultures.length < 2}
						items={supportedLanguages}
						onApply={(value) => {
							setSelectedCultureCode(value as string);
						}}
						selectedValue={selectedCultureCode}
					/>
				</LanguageSelectContainer>
				<ClearDiv clear Height={'15px'} />
				<div>{HtmlParser(surveyTermState?.pageDescription?.textValue ?? '')}</div>
				<ClearDiv clear Height={'15px'} />
				<EditableTable id='survey-definitions-preview-table' columns={columns} editMode={false} responsive={true} data={items} />
			</Content>
		</Modal>
	);
}
